import { StandardEvents } from '@shopify/events';

const actions = 'product-form-component, solstice-rx-launcher, solstice-vto-button';

class VariantLoading extends HTMLElement {
  #generation = 0;
  #section;
  #locked = false;
  #owned = new Set();
  #observer = new MutationObserver(() => this.#lockActions());

  connectedCallback() {
    this.#section = this.closest('.shopify-section');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#select);
    this.#section?.addEventListener('click', this.#guard, true);
    this.#section?.addEventListener('submit', this.#guard, true);
    this.querySelector('button')?.addEventListener('click', this.#reload);
  }

  disconnectedCallback() {
    ++this.#generation;
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#select);
    this.#section?.removeEventListener('click', this.#guard, true);
    this.#section?.removeEventListener('submit', this.#guard, true);
    this.querySelector('button')?.removeEventListener('click', this.#reload);
    this.#unlock();
  }

  #reload = () => window.location.reload();

  #guard = (event) => {
    if (this.#locked && event.target instanceof Element && event.target.closest(actions)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };

  #lockActions() {
    if (!this.#locked) return;
    this.#section?.querySelectorAll(actions).forEach((element) => {
      if (!element.hasAttribute('inert')) {
        this.#owned.add(element);
        element.setAttribute('inert', '');
      }
    });
  }

  #unlock() {
    this.#observer.disconnect();
    this.#locked = false;
    this.#owned.forEach((element) => element.removeAttribute('inert'));
    this.#owned.clear();
    this.#section?.removeAttribute('data-variant-loading');
    this.#section?.removeAttribute('aria-busy');
  }

  #select = async (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('.solstice__horizon-product-wrapper')) return;
    const generation = ++this.#generation;
    this.#locked = true;
    this.hidden = false;
    this.dataset.state = 'loading';
    this.querySelector('[role="status"]').textContent = 'Updating selection…';
    this.querySelector('button').hidden = true;
    this.#section.setAttribute('data-variant-loading', '');
    this.#section.setAttribute('aria-busy', 'true');
    this.#lockActions();
    this.#observer.observe(this.#section, { childList: true, subtree: true, attributes: true, attributeFilter: ['inert'] });
    try {
      await event.promise;
      // Let the existing price, gallery and purchase subscribers finish their morphs.
      await new Promise((resolve) => requestAnimationFrame(resolve));
      if (generation !== this.#generation) return;
      this.#unlock();
      this.hidden = true;
    } catch {
      if (generation !== this.#generation) return;
      this.#section.removeAttribute('aria-busy');
      this.dataset.state = 'error';
      this.querySelector('[role="status"]').textContent = 'Could not update this selection. Choose another option or reload to try again.';
      this.querySelector('button').hidden = false;
      // Keep stale purchase targets inert until a successful selection or reload.
    }
  };
}

if (!customElements.get('solstice-variant-loading')) {
  customElements.define('solstice-variant-loading', VariantLoading);
}
